import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { logError } from '@/lib/log';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '@shared/services/firebase';
import { useAuth } from './AuthContext';
import { createPortalSession } from '@shared/services/stripeService';
import { identifyUser, trackEvent } from '@shared/services/analyticsTracker';
import { trackPaidConversion } from '@shared/services/conversionTracking';
import { recordSubscriptionAttribution } from '@shared/services/attributionService';
import {
  deriveEntitlementFlags,
  resolveEntitlementIds,
  PRODUCT_LINE_PRICES,
  SPENDING_TOOLS_ENTITLEMENTS,
} from '@shared/constants/entitlements';
import { WEB_PRICES } from '../lib/paidLaunch';
import { recordPaywallFunnelEvent } from '@/lib/paywallFunnelEvents';
import type { CustomerSubscriptionInfo, EntitlementId, ProductLine, SubscriptionStore } from '@shared/types';
import { normalizeSubscriptionStore } from '@shared/types';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { DEVICE_STORES, getActiveEntitlements, initPurchases, loginPurchases, logoutPurchases } from '../services/purchasesService';

// Reverse lookup: Stripe price ID → dollar amount (built from env vars at module load)
const priceIdToAmount: Record<string, number> = {};
for (let amt = 4; amt <= 12; amt++) {
  const priceId = import.meta.env[`VITE_STRIPE_MONTHLY_PRICE_ID_${amt}`];
  if (priceId) priceIdToAmount[priceId] = amt;
}
// Paid-launch prices (T2): without these, every $15/$100 spending_tools
// purchase reported value 0 to GA4/ads — corrupting the §9 paid-CPS readout.
if (WEB_PRICES.monthly.priceId) priceIdToAmount[WEB_PRICES.monthly.priceId] = WEB_PRICES.monthly.amount;
if (WEB_PRICES.annual.priceId) priceIdToAmount[WEB_PRICES.annual.priceId] = WEB_PRICES.annual.amount;

/** Monthly price for an entitlement tier — the fallback when the product id
 *  isn't in the Stripe map (native purchases). Mirrors the SoT price map. */
const tierMonthlyValue = (tier: string): number => {
  if (tier === 'all_tools') return PRODUCT_LINE_PRICES.all;
  if (tier === 'debt_tools') return PRODUCT_LINE_PRICES.debt;
  if (SPENDING_TOOLS_ENTITLEMENTS.has(tier)) return PRODUCT_LINE_PRICES.spending;
  return 0;
};

/** Whether a subscription-info shape grants any paid product line — the same
 *  SoT derivation the render uses, applied to a candidate state. */
const isPremiumInfo = (info: CustomerSubscriptionInfo | null): boolean =>
  deriveEntitlementFlags(resolveEntitlementIds(info)).isPremium;

interface PaywallState {
  isOpen: boolean;
  /** Which surface opened the paywall — forwarded to the impression analytics. */
  source?: string;
  /** Which product line the paywall should lead with (three-product scaffolding). */
  product?: ProductLine;
  onPurchaseComplete?: () => void;
  onPurchaseError?: (error: Error) => void;
  onDismiss?: () => void;
}

interface SubscriptionContextValue {
  subscriptionInfo: CustomerSubscriptionInfo | null;
  /** True if user has any active subscription (any paid product line) */
  isPremium: boolean;
  /** True for any paid key — data connections come with the one product.
   *  @deprecated prefer hasSpendingTools / hasDebtTools for feature gating */
  isDataConnect: boolean;
  /** True when the user's entitlements grant the spending tools (every paid key). */
  hasSpendingTools: boolean;
  /** True when the user's entitlements grant the debt tools — every paid key
   *  since 2026-10-07 (one product). The /debt page itself stays behind the
   *  debt_manager_enabled Remote Config flag. */
  hasDebtTools: boolean;
  /** True only when user has the legacy early_access entitlement */
  isEarlyAccess: boolean;
  /** The highest active entitlement, or null if none */
  highestEntitlement: EntitlementId | null;
  loading: boolean;
  /** True when subscription info has been fetched */
  isReady: boolean;
  error: string | null;
  refreshSubscription: () => Promise<boolean>;
  /** Opens the Stripe Customer Portal for managing the subscription */
  openManageSubscription: () => Promise<void>;
  /** Which billing surface owns the live subscription, resolved fresh (see
   *  openManageSubscription for why the mirrored doc's `store` can be stale).
   *  Callers that need to tell the user who bills them — account deletion,
   *  manage-subscription — should use this rather than subscriptionInfo.store. */
  resolveBillingStore: () => Promise<SubscriptionStore | undefined>;
  // Paywall methods
  showPaywall: (options?: {
    /** Which surface triggered the paywall (e.g. 'debt_manager', 'connect'). */
    source?: string;
    /** Which product line the paywall should lead with (three-product scaffolding). */
    product?: ProductLine;
    onPurchaseComplete?: () => void;
    onPurchaseError?: (error: Error) => void;
    onDismiss?: () => void;
  }) => void;
  hidePaywall: () => void;
  paywallState: PaywallState;
  /**
   * Paywall UIs call this when the user initiates checkout (price picked /
   * subscribe tapped). Part of the paywall seam contract — it stamps the
   * 'purchase_started' funnel event with the open paywall's source/product,
   * so a reshaped paywall only has to keep calling it.
   */
  notifyPaywallPurchaseStarted: () => void;
}

/** @internal Exported for demo mode — do not use directly in app components */
export const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

// Response type from Cloud Function
interface EntitlementsResponse {
  highestEntitlement: EntitlementId | null;
  entitlementLevel?: string;
  activeEntitlements: EntitlementId[];
  hasActiveSubscription: boolean;
  expirationDate: string | null;
  productId: string | null;
  /** null = the CF couldn't determine it (subscriptions lookup failed). */
  isTrial?: boolean | null;
  /** Additive (2026-09): which truth answered — the live RevenueCat read or
   *  the enoughRevenuecatCustomers mirror. Absent on older function deploys. */
  source?: 'rc_api' | 'mirror';
  /** Additive (2026-09): the RevenueCat read did not complete cleanly, so
   *  the answer is best-available, not authoritative. Absent on older deploys. */
  degraded?: boolean;
}

// Shape of the Firestore enoughRevenuecatCustomers/{uid} document
interface FirestoreCustomerDoc {
  status: string;
  entitlementLevel?: string;
  activeEntitlementIds: string[];
  hasActiveSubscription: boolean;
  activeProductId?: string;
  expirationDate?: string;
  willRenew?: boolean;
  isTrial?: boolean;
  store?: string;
  updatedAt?: unknown;
}

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [subscriptionInfo, setSubscriptionInfo] = useState<CustomerSubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paywallState, setPaywallState] = useState<PaywallState>({ isOpen: false });

  // Precedence between the two entitlement writers (2026-07-19): the
  // enoughRevenuecatCustomers/{uid} onSnapshot mirror and the
  // enough_get_entitlements CF fetch race on boot, and last-write-wins let a
  // CF response landing after the snapshot downgrade a real member to free —
  // that walled a paying customer behind PaidAccessGate. Once the mirror has delivered a customer doc for this uid,
  // it is authoritative; the CF fetch may only UPGRADE (grant premium while
  // in-memory state is not premium — the restore/purchase mirror-lag case).
  // uid-keyed so a signed-out→signed-in uid change can't inherit authority.
  const snapshotDocUidRef = useRef<string | null>(null);
  // Render-synced mirror of subscriptionInfo so the async fetch callback can
  // read the CURRENT state without re-creating itself on every state change.
  const subscriptionInfoRef = useRef<CustomerSubscriptionInfo | null>(null);
  subscriptionInfoRef.current = subscriptionInfo;

  // Fetch entitlements from Cloud Function — returns true if user has an active subscription
  // Also seeds enoughRevenuecatCustomers/{uid} in Firestore for onSnapshot
  const fetchEntitlements = useCallback(async (): Promise<boolean> => {
    // Anonymous visitors (every visitor gets an anonymous account — see
    // AuthContext) can't subscribe and only see the pre-login entry. Skip
    // the entitlements Cloud Function call and treat them like a signed-out user.
    if (!currentUser || currentUser.isAnonymous) {
      setSubscriptionInfo(null);
      setLoading(false);
      setIsReady(false);
      setError(null);
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const getEntitlements = httpsCallable<Record<string, never>, EntitlementsResponse>(
        functions,
        'enough_get_entitlements'
      );

      const result = await getEntitlements({});
      const data = result.data;

      const info: CustomerSubscriptionInfo = {
        status: data.highestEntitlement ?? 'free',
        entitlementLevel: data.entitlementLevel,
        activeEntitlementIds: data.activeEntitlements,
        hasActiveSubscription: data.hasActiveSubscription,
        activeProductId: data.productId ?? undefined,
        expirationDate: data.expirationDate ?? undefined,
        isTrial: data.isTrial ?? undefined,
        lastUpdated: new Date().toISOString(),
      };

      const mirrorIsAuthoritative = snapshotDocUidRef.current === currentUser.uid;
      const currentIsPremium = isPremiumInfo(subscriptionInfoRef.current);
      // Apply the CF result when it's the seed (no mirror doc yet) or an
      // upgrade to premium (post-restore/post-purchase, before the webhook
      // lands in the mirror). Never let it downgrade a mirror-backed state —
      // and never let a premium-shaped CF overwrite drop mirror-only fields
      // (isTrial/store/willRenew) from an already-premium state.
      if (!mirrorIsAuthoritative || (isPremiumInfo(info) && !currentIsPremium)) {
        setSubscriptionInfo(info);
        setIsReady(true);
      }

      // Best knowledge across both sources: a member the mirror knows about
      // must never be reported free because one CF response disagreed.
      return data.hasActiveSubscription || (mirrorIsAuthoritative && currentIsPremium);
    } catch (err) {
      logError(err, { context: 'subscription_context_fetch_entitlements', tags: { operation: 'fetch_entitlements' } });
      setError('Failed to load subscription status');

      const currentIsPremium = isPremiumInfo(subscriptionInfoRef.current);
      // Fail-open 'unknown' state on error so the app can still function —
      // but only as the seed. A mirror-backed or premium state stands; an
      // errored CF call must not flip a known member to 'unknown'.
      if (snapshotDocUidRef.current !== currentUser.uid && !currentIsPremium) {
        setSubscriptionInfo({
          status: 'unknown',
          activeEntitlementIds: [],
          hasActiveSubscription: false,
          lastUpdated: new Date().toISOString(),
        });
        setIsReady(true);
      }
      return currentIsPremium;
    } finally {
      setLoading(false);
    }
  // Value-based deps: linkWithCredential signups mutate the same User object
  // (no onAuthStateChanged), so depending on the object reference would leave
  // entitlements unfetched after an anonymous→email signup.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.isAnonymous]);

  // Initialize RevenueCat SDK on native platforms when user is available.
  //
  // ANONYMOUS uids are identified too — that is what lets the value
  // onboarding carousel sell a trial BEFORE the login screen: the purchase lands on the anonymous Firebase uid, and because
  // every new-user signup path preserves that uid (email links via
  // linkWithCredential; Google/Apple link-first via promoteOrSignIn), the
  // entitlement is already attached to the account the user ends up with —
  // no RevenueCat transfer needed. A RETURNING user who signs in to an
  // existing account instead gets a different uid; their store purchase
  // follows them via restorePurchases (RC transfers entitlements to the
  // current app user on restore).
  useEffect(() => {
    if (!currentUser) {
      logoutPurchases();
      return;
    }
    initPurchases(currentUser.uid).then(() => {
      loginPurchases(currentUser.uid);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.isAnonymous]);

  // Initial fetch to seed the Firestore doc (also serves as fallback)
  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  // Real-time listener on enoughRevenuecatCustomers/{uid}
  // This is the primary update path — webhook → Firestore → onSnapshot → state
  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous) return;

    const customerRef = doc(db, 'enoughRevenuecatCustomers', currentUser.uid);

    const unsubscribe = onSnapshot(
      customerRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          // No doc yet — Cloud Function fetch will seed it
          return;
        }

        // The mirror has spoken for this uid — from here on the CF fetch may
        // only upgrade, never clobber (see snapshotDocUidRef above).
        snapshotDocUidRef.current = currentUser.uid;

        const data = snapshot.data() as FirestoreCustomerDoc;

        const info: CustomerSubscriptionInfo = {
          status: (data.status as CustomerSubscriptionInfo['status']) ?? 'free',
          entitlementLevel: data.entitlementLevel,
          activeEntitlementIds: (data.activeEntitlementIds ?? []) as EntitlementId[],
          hasActiveSubscription: data.hasActiveSubscription ?? false,
          activeProductId: data.activeProductId,
          expirationDate: data.expirationDate,
          willRenew: data.willRenew,
          isTrial: data.isTrial,
          store: normalizeSubscriptionStore(data.store),
          lastUpdated: new Date().toISOString(),
        };

        setSubscriptionInfo(info);
        setIsReady(true);
        setLoading(false);
        setError(null);
      },
      (err) => {
        logError(err, { context: 'subscription_context_on_snapshot' });
        // Don't overwrite state — Cloud Function fetch is the fallback
      }
    );

    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.isAnonymous]);

  // Refresh subscription — returns true if user has an active subscription
  const refreshSubscription = useCallback(async (): Promise<boolean> => {
    return fetchEntitlements();
  }, [fetchEntitlements]);

  // Open subscription management — routes to App Store/Play Store settings
  // for native purchases, or Stripe Customer Portal for web purchases.
  //
  // On native, the RevenueCat SDK is asked FIRST: the Firestore doc's `store`
  // is written by the last webhook to land, and after a subscription TRANSFER
  // (restore/sign-in to a different account) it can keep pointing at a dead
  // Stripe sub until the next renewal event — which sent an App Store
  // subscriber to the Stripe portal instead of their phone's subscription
  // settings (2026-07-17 TestFlight run). The SDK's CustomerInfo reflects the
  // transfer immediately and can't be stale in that way.
  const resolveBillingStore = useCallback(async (): Promise<SubscriptionStore | undefined> => {
    let store = subscriptionInfo?.store;

    if (Capacitor.isNativePlatform()) {
      try {
        const deviceEntitlements = await getActiveEntitlements();
        const active = deviceEntitlements.find((e) => DEVICE_STORES.includes(e.store));
        if (active) store = normalizeSubscriptionStore(active.store);
      } catch { /* fall back to the doc's store */ }
    }

    return store;
  }, [subscriptionInfo?.store]);

  const openManageSubscription = useCallback(async () => {
    const store = await resolveBillingStore();

    if (store === 'app_store') {
      await Browser.open({ url: 'https://apps.apple.com/account/subscriptions' });
      return;
    }

    if (store === 'play_store') {
      await Browser.open({ url: 'https://play.google.com/store/account/subscriptions' });
      return;
    }

    // Stripe or unknown — open Stripe Customer Portal
    const url = await createPortalSession();
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url });
    } else {
      window.location.href = url;
    }
  }, [resolveBillingStore]);

  // Tracks whether the currently open paywall converted, so hidePaywall can
  // tell a dismissal apart from closing the post-purchase success screen.
  const paywallPurchaseCompletedRef = useRef(false);

  // Show custom paywall modal
  const showPaywall = useCallback((options?: {
    source?: string;
    product?: ProductLine;
    onPurchaseComplete?: () => void;
    onPurchaseError?: (error: Error) => void;
    onDismiss?: () => void;
  }) => {
    const source = options?.source;
    const product = options?.product;
    paywallPurchaseCompletedRef.current = false;
    recordPaywallFunnelEvent(currentUser, 'impression', { source, product });
    setPaywallState({
      isOpen: true,
      source,
      product,
      // Wrapped so 'purchase_completed' fires even when the trigger passed no
      // callback — the paywall UI invokes this on success either way.
      onPurchaseComplete: () => {
        paywallPurchaseCompletedRef.current = true;
        recordPaywallFunnelEvent(currentUser, 'purchase_completed', { source, product });
        options?.onPurchaseComplete?.();
      },
      onPurchaseError: options?.onPurchaseError,
      onDismiss: options?.onDismiss,
    });
  }, [currentUser]);

  // Hide custom paywall modal
  const hidePaywall = useCallback(() => {
    const { isOpen, source, product, onDismiss } = paywallState;
    if (isOpen && !paywallPurchaseCompletedRef.current) {
      recordPaywallFunnelEvent(currentUser, 'dismissed', { source, product });
    }
    setPaywallState({ isOpen: false });
    onDismiss?.();
  }, [paywallState, currentUser]);

  // Paywall UIs signal checkout initiation through this (seam contract) —
  // the source/product dimensions come from the open paywall state.
  const notifyPaywallPurchaseStarted = useCallback(() => {
    if (!paywallState.isOpen) return;
    recordPaywallFunnelEvent(currentUser, 'purchase_started', {
      source: paywallState.source,
      product: paywallState.product,
    });
  }, [paywallState, currentUser]);

  // Derived gate booleans — the one shared derivation (constants/entitlements.ts).
  // One product: every paid key grants spending AND debt tools. Legacy Payoff
  // Genius 'pro' is retired (grants nothing).
  const { hasSpendingTools, hasDebtTools, isDataConnect, isEarlyAccess, isPremium, highestEntitlement } =
    deriveEntitlementFlags(resolveEntitlementIds(subscriptionInfo));

  // Track subscription changes for analytics
  // null = not yet hydrated (first load), false/true = observed state
  const prevHadSubscription = useRef<boolean | null>(null);
  const prevWasTrial = useRef(false);
  useEffect(() => {
    if (!currentUser || !isReady) return;

    const tier = highestEntitlement ?? 'free';
    const isTrial = subscriptionInfo?.isTrial === true;

    // Update GA4 + Fullstory + PostHog user properties with current subscription tier
    identifyUser(currentUser.uid, {
      subscription_tier: tier,
      platform: Capacitor.getPlatform() as 'web' | 'ios' | 'android',
      email: currentUser.email ?? '',
    });

    // On first hydration, seed the refs without firing conversion events —
    // existing paid users should not be counted as fresh purchases on every launch
    if (prevHadSubscription.current === null) {
      prevHadSubscription.current = isPremium;
      prevWasTrial.current = isPremium && isTrial;
      return;
    }

    // Resolve price: Stripe price ID lookup first, then the SoT tier price for
    // native purchases (never 0 for a real paid line).
    const productId = subscriptionInfo?.activeProductId;
    const purchaseValue = (productId && priceIdToAmount[productId]) || tierMonthlyValue(tier);

    const firePurchase = () => {
      // GA4 standard purchase event — enables monetization reports
      trackEvent('purchase', {
        currency: 'USD',
        value: purchaseValue,
        transaction_id: `${currentUser.uid}_${Date.now()}`,
        item_name: tier,
        item_category: 'subscription',
      });
      // `uid` stamps the conversion's `transaction_id` so Google Ads can
      // dedupe it and join it to the offline conversion feed. This is the
      // ONLY site that can fire a trial→paid conversion — the paywall is
      // long gone by the time the trial converts — so without it the real
      // revenue moment is the one conversion arriving with no join key.
      trackPaidConversion({ value: purchaseValue, uid: currentUser.uid });
    };

    // Trial-vs-paid split (2026-07-19): with a card-required 30-day trial, the
    // free→premium flip happens at TRIAL START, not first payment. Counting it
    // as `purchase`/`conversion_paid` teaches Google Ads to optimize toward
    // trial-starts that may never pay. So: trial start fires its own event;
    // `purchase` fires when money actually moves — a direct paid start, or the
    // moment the trial converts (isTrial flips false while still premium).
    if (isPremium && !prevHadSubscription.current) {
      // Promotional grants (grandfather comps) are gifts, not conversions —
      // never teach the ad platforms a $ value from them.
      if (subscriptionInfo?.store === 'promotional') {
        trackEvent('subscription_started', { tier, is_trial: false, promotional: true });
        prevHadSubscription.current = true;
        prevWasTrial.current = false;
        return;
      }
      // New-purchase conversion events fire at the POINT OF SALE
      // (SpendingToolsPaywall → trackSubscriptionPurchase), never here
      // (2026-07-30). The launch funnel buys on an ANONYMOUS uid before
      // sign-in: this observer only hydrates post-signup, where its first
      // sight of the user is already-premium (the first-hydration seeding
      // above), so it can never see that flip — GA showed 8 real trials and
      // zero events. And the flips it CAN see are the wrong ones to count:
      // firing here would double the authed in-app purchase paths the paywall
      // already tracks, and a restore-driven flip is not a purchase at all.
      // This branch only backstops funnel attribution (idempotent write) and
      // falls through to hold the trial memory for the trial→paid flip below.
      recordSubscriptionAttribution(currentUser.uid).catch(logError);
    } else if (isPremium && prevWasTrial.current && subscriptionInfo?.isTrial === false) {
      // Trial → paid conversion: the real payment moment. Explicit `false`
      // only — an undefined isTrial (write that dropped the field) must not
      // mint a paid conversion.
      trackEvent('trial_converted', { tier });
      firePurchase();
    }
    prevHadSubscription.current = isPremium;
    // An unknown isTrial on a still-premium write must not erase the trial
    // memory — otherwise the later explicit isTrial:false flip can't be
    // recognized as the trial→paid conversion.
    if (!isPremium) {
      prevWasTrial.current = false;
    } else if (subscriptionInfo?.isTrial !== undefined) {
      prevWasTrial.current = isTrial;
    }
  }, [currentUser, isReady, isPremium, highestEntitlement, subscriptionInfo?.isTrial, subscriptionInfo?.store]);

  const value: SubscriptionContextValue = {
    subscriptionInfo,
    isPremium,
    isDataConnect,
    hasSpendingTools,
    hasDebtTools,
    isEarlyAccess,
    highestEntitlement,
    loading,
    isReady,
    error,
    refreshSubscription,
    openManageSubscription,
    resolveBillingStore,
    showPaywall,
    hidePaywall,
    paywallState,
    notifyPaywallPurchaseStarted,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextValue => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return ctx;
};
